import React from 'react';
import styled from 'styled-components';

import { useMediaQuery } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { VenuePlan } from '../../graphql/API';
import { useAdminGetDashboardModelsStatsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardModelsStatsDataQuery';
import { useAdminGetDashboardReservationsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardReservationsDataQuery';
import { useAdminGetDashboardVenuesStatsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardVenuesStatsDataQuery';
import { calculatePercentageChange } from '../../utils/dashboard';
import { VENUE_MANUAL_TAGS } from '../../utils/manual-tags';
import ActiveCard from './ActiveCard';
import { CardError } from './CardError';
import { PackageCard } from './PackageCard';
import { withMarket } from './dashboardUtils';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  marketID: string | null;
  selectedLabel: string;
};

// Renders a placeholder instead of "undefined"/"null" while data is missing
const formatCount = (value?: number | null): string => (value == null ? '—' : String(value));

// Backend totals include the plans assigned for free by the admin; show the real paid count
const formatPaidCount = (total?: number | null, assignedByAdmin?: number | null): string =>
  total == null ? '—' : String(Math.max(total - (assignedByAdmin || 0), 0));

export const Totals: React.FC<Props> = ({
  fromDate,
  priorFrom,
  priorTo,
  toDate,
  marketID,
  selectedLabel,
}) => {
  // Must match the HideOnMobile breakpoint in HomePage.tsx (487px), or at exactly 487px
  // the desktop sections are hidden while this component still renders its desktop branch
  const isMobileScreen = useMediaQuery('(max-width:487px)');

  const {
    data: reservationsData,
    isFetching: reservationDataIsFetching,
    isError: reservationsIsError,
    refetch: reservationsRefetch,
  } = useAdminGetDashboardReservationsDataQuery(
    {
      fromDate: fromDate || '',
      toDate: toDate || '',
      priorFromDate: priorFrom || '',
      priorToDate: priorTo || '',
      marketID: marketID,
    },
    isMobileScreen,
  );

  const {
    data,
    isFetching,
    isError: venuesStatsIsError,
    refetch: venuesStatsRefetch,
  } = useAdminGetDashboardVenuesStatsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
  });
  const {
    data: modelsStatsData,
    isFetching: modelsStatsIsFetching,
    isError: modelsStatsIsError,
    refetch: modelsStatsRefetch,
  } = useAdminGetDashboardModelsStatsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID: marketID || '',
  });

  const totalUniqueReservationPercentage = calculatePercentageChange(
    reservationsData?.totalUniqueUserReservationsCurrent || 0,
    reservationsData?.totalUniqueUserReservationsPrior || 0,
  );

  const totalReservations = React.useMemo(
    () =>
      (reservationsData?.total1StoryReservationsCurrent || 0) +
      (reservationsData?.total3StoriesReservationsCurrent || 0) +
      (reservationsData?.totalFeedReservationsCurrent || 0) +
      (reservationsData?.totalReelsReservationsCurrent || 0),
    [reservationsData],
  );

  const totalPriorReservations = React.useMemo(
    () =>
      (reservationsData?.total1StoryReservationsPrior || 0) +
      (reservationsData?.total3StoriesReservationsPrior || 0) +
      (reservationsData?.totalFeedReservationsPrior || 0) +
      (reservationsData?.totalReelsReservationsPrior || 0),
    [reservationsData],
  );

  const { totalPercentageReservations } = React.useMemo(() => {
    const totalPercentageReservations = calculatePercentageChange(
      totalReservations,
      totalPriorReservations,
    );

    return {
      totalPercentageReservations,
    };
  }, [reservationsData]);

  const totalCurrentActiveUsersByPeriod = React.useMemo(
    () =>
      (modelsStatsData?.totalPremiumUsers || 0) -
      (modelsStatsData?.totalCurrentActivePremiumUsers || 0) +
      (modelsStatsData?.totalCurrentExpiredPremiumUsers || 0),
    [modelsStatsData],
  );

  const totalPriorActiveUsersByPeriod = React.useMemo(
    () =>
      (modelsStatsData?.totalPremiumUsers || 0) -
      (modelsStatsData?.totalPriorActivePremiumUsers || 0) +
      (modelsStatsData?.totalPriorExpiredPremiumUsers || 0),
    [modelsStatsData],
  );

  return (
    <>
      {isMobileScreen ? (
        <>
          <AllStats>
            <Heading>Dashboard</Heading>
          </AllStats>
          {modelsStatsIsError && !modelsStatsIsFetching && (
            <CardError onRetry={() => modelsStatsRefetch()} />
          )}
          {venuesStatsIsError && !isFetching && <CardError onRetry={() => venuesStatsRefetch()} />}
          {reservationsIsError && !reservationDataIsFetching && (
            <CardError onRetry={() => reservationsRefetch()} />
          )}
          <ActiveCard
            bgColor="#FDF1E6"
            count={formatCount(modelsStatsData?.totalActiveModelsLastMonth)}
            label="Active models"
            link={withMarket('/users', marketID)}
          />
          <PackageContainer>
            <PackageCard
              isMobile
              bgColor="#FDF1E6"
              count={formatCount(modelsStatsData?.totalPremiumUsers)}
              label="Premium models"
            />
            <PackageCard
              isMobile
              bgColor="#FDF1E6"
              count={formatCount(modelsStatsData?.totalScoutingUsers)}
              label="Scouting subscription"
            />
            <PackageCard
              isMobile
              bgColor="#FDF1E6"
              count={formatCount(modelsStatsData?.totalCredits)}
              label="Credits purchased"
            />
          </PackageContainer>
          <ActiveCard
            bgColor="#E6EAFD"
            count={formatCount(data?.totalActiveVenues)}
            label="Active venues"
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}`,
              marketID,
            )}
          />
          <PackageContainer>
            <PackageCard
              link={withMarket(
                `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.free}`,
                marketID,
              )}
              isMobile
              bgColor="#FDF1E6"
              count={formatCount(data?.totalFreeVenuesWithoutOffers)}
              label="Free"
            />
            <PackageCard
              link={withMarket(
                `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`,
                marketID,
              )}
              isMobile
              bgColor="#FDF1E6"
              count={formatPaidCount(
                data?.totalStarterVenuesWithoutOffers,
                data?.totalStarterFreeVenues,
              )}
              label="Starter"
            />
            <PackageCard
              link={withMarket(
                `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`,
                marketID,
              )}
              isMobile
              bgColor="#FDF1E6"
              count={formatPaidCount(data?.totalPlusVenuesWithoutOffers, data?.totalPlusFreeVenues)}
              label="Plus"
            />
          </PackageContainer>

          <ActiveCard
            bgColor="#E6EAFD"
            count={formatCount(data?.totalActiveBrands)}
            label="Active brands subscribed"
            link={withMarket(`/businesses?tags=${VENUE_MANUAL_TAGS.Online}`, marketID)}
          />
          <PackageContainer>
            <PackageCard
              link={withMarket(
                `/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.basic}`,
                marketID,
              )}
              isMobile
              bgColor="#FDF1E6"
              count={formatPaidCount(
                data?.totalBasicBrandsWithoutOffers,
                data?.totalBasicFreeBrands,
              )}
              label="Basic"
            />
            <PackageCard
              link={withMarket(
                `/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`,
                marketID,
              )}
              isMobile
              bgColor="#FDF1E6"
              count={formatPaidCount(
                data?.totalStarterBrandsWithoutOffers,
                data?.totalStarterFreeBrands,
              )}
              label="Starter"
            />
            <PackageCard
              link={withMarket(
                `/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`,
                marketID,
              )}
              isMobile
              bgColor="#FDF1E6"
              count={formatPaidCount(data?.totalPlusBrandsWithoutOffers, data?.totalPlusFreeBrands)}
              label="Plus"
            />
          </PackageContainer>
          <AllStats>
            <Heading>All Stats</Heading>
            <Time>{selectedLabel}</Time>
          </AllStats>
          <StatCard
            name="Reservations"
            count={String(totalReservations)}
            compare={`${totalPercentageReservations}%`}
            percentageNumber={totalPercentageReservations}
          />
          <StatCard
            isFetching={reservationDataIsFetching}
            name="Unique users making a reservation"
            count={String(reservationsData?.totalUniqueUserReservationsCurrent || 0)}
            compare={`${totalUniqueReservationPercentage}%`}
            isNegative={totalUniqueReservationPercentage < 0}
          />
        </>
      ) : (
        <>
          <PackageContainerDesktop>
            {modelsStatsIsError && !modelsStatsIsFetching && (
              <FullRow>
                <CardError onRetry={() => modelsStatsRefetch()} />
              </FullRow>
            )}
            {(modelsStatsIsFetching || isFetching) && (
              <>
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={'100%'}
                  height={100}
                  style={{ borderRadius: '8px' }}
                />
              </>
            )}
            {!modelsStatsIsFetching && !isFetching && modelsStatsData && (
              <>
                <ActiveCard
                  showComparison
                  bgColor="#FDF1E6"
                  count={formatCount(modelsStatsData?.totalActiveModelsLastMonth)}
                  label="Active models"
                  link={withMarket('/users', marketID)}
                  selectedLabel={selectedLabel}
                  comparison={calculatePercentageChange(
                    modelsStatsData?.totalActiveModelsCurrentMonth || 0,
                    modelsStatsData?.totalActiveModelsPriorMonth || 0,
                  )}
                />
                <PackageCard
                  link={withMarket('/users?premium=true', marketID)}
                  showFooter
                  showComparison
                  selectedLabel={selectedLabel}
                  bgColor="#FDF1E6"
                  count={formatCount(modelsStatsData?.totalPremiumUsers)}
                  label="Premium models"
                  newCount={modelsStatsData?.totalNewPremiumUsers || 0}
                  canceledCount={modelsStatsData?.totalPremiumExpiredUsers || 0}
                  newByPriceList={modelsStatsData?.newPremiumUsersByPriceList}
                  canceledByPriceList={modelsStatsData?.premiumExpiredUsersByPriceList}
                  comparison={calculatePercentageChange(
                    modelsStatsData?.totalCurrentPremiumUsers || 0,
                    modelsStatsData?.totalPriorPremiumUsers || 0,
                  )}
                />
                <PackageCard
                  showFooter
                  selectedLabel={selectedLabel}
                  bgColor="#FDF1E6"
                  count={formatCount(modelsStatsData?.totalScoutingUsers)}
                  label="Scouting subscription"
                  newCount={modelsStatsData?.totalNewScoutingUsers || 0}
                  canceledCount={modelsStatsData?.totalScoutingExpiredUsers || 0}
                />
                <PackageCard
                  showComparison
                  selectedLabel={selectedLabel}
                  bgColor="#FDF1E6"
                  count={formatCount(modelsStatsData?.totalCredits)}
                  label="Credits purchased"
                  comparison={calculatePercentageChange(
                    modelsStatsData?.totalCurrentCredits || 0,
                    modelsStatsData?.totalPriorCredits || 0,
                  )}
                />
              </>
            )}
          </PackageContainerDesktop>
        </>
      )}
    </>
  );
};

const PackageContainerDesktop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 16px;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const PackageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 15px 0px 48px;
`;

const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;
